// HeroCard — the ONE gradient hero every agent-end panel uses (U1, 2026-07-09).
//
// The permanent fix for the hand-rolled-hero bug class: each panel used to
// build its own gradient card, and each was a fresh chance to pick colors
// that only worked in one theme, or to gate visibility behind a JS opacity
// entrance (which freezes when rAF is throttled).
//
// Design contract:
//   * SATURATED gradient (from-500 to-700/800) + white text — reads
//     identically in light and dark mode by construction. Never a pale
//     tinted gradient with theme-dependent text.
//   * No JS entrance animation. Content is visible from first paint.
//   * One decorative blur highlight for the luxury finish.
//
// Usage:
//   <HeroCard tone="brand" icon="account_balance_wallet"
//             kicker="Pool balance" value={`₹${n.toLocaleString('en-IN')}`}
//             sub="Available for player recharges." right={<Badge/>}>
//     {optional extra rows}
//   </HeroCard>

import Icon from '../../components/Icon';

export type HeroTone = 'brand' | 'emerald' | 'rose' | 'amber' | 'indigo' | 'sky' | 'violet';

const TONE: Record<HeroTone, string> = {
  brand:   'bg-gradient-to-br from-brand-500 to-brand-700 ring-brand-300/40 shadow-[0_10px_30px_-12px_rgba(var(--brand-rgb),0.55)]',
  emerald: 'bg-gradient-to-br from-emerald-500 to-emerald-700 ring-emerald-300/40 shadow-[0_10px_30px_-12px_rgba(16,185,129,0.5)]',
  rose:    'bg-gradient-to-br from-rose-500 to-rose-700 ring-rose-300/40 shadow-[0_10px_30px_-12px_rgba(244,63,94,0.55)]',
  amber:   'bg-gradient-to-br from-amber-500 to-amber-700 ring-amber-300/40 shadow-[0_10px_30px_-12px_rgba(245,158,11,0.5)]',
  indigo:  'bg-gradient-to-br from-indigo-500 to-indigo-700 ring-indigo-300/40 shadow-[0_10px_30px_-12px_rgba(99,102,241,0.55)]',
  sky:     'bg-gradient-to-br from-sky-500 to-sky-700 ring-sky-300/40 shadow-[0_10px_30px_-12px_rgba(14,165,233,0.5)]',
  violet:  'bg-gradient-to-br from-violet-500 to-violet-700 ring-violet-300/40 shadow-[0_10px_30px_-12px_rgba(139,92,246,0.55)]',
};

export default function HeroCard({
  tone = 'brand', icon, kicker, value, sub, right, children, className = '',
}: {
  tone?: HeroTone;
  icon?: string;
  /** Small uppercase label above the value. */
  kicker?: string;
  /** The one big thing this card says (string or rich node). */
  value: React.ReactNode;
  /** One plain-words sentence under the value. */
  sub?: React.ReactNode;
  /** Top-right slot (status badge, chip…). */
  right?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={`relative overflow-hidden rounded-2xl p-5 ring-1 text-white ${TONE[tone]} ${className}`}>
      <div aria-hidden className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-white/15 blur-2xl" />
      <div className="relative">
        {(icon || kicker || right) && (
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <div className="flex items-center gap-2 min-w-0">
              {icon && <Icon name={icon} size={16} className="text-white/90 shrink-0" />}
              {kicker && (
                <span className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-white/90 truncate">
                  {kicker}
                </span>
              )}
            </div>
            {right}
          </div>
        )}
        <div className="font-display font-black text-4xl sm:text-5xl tabular-nums leading-none">
          {value}
        </div>
        {sub && <p className="mt-2 text-[12.5px] leading-snug text-white/85">{sub}</p>}
        {children}
      </div>
    </section>
  );
}
